import React, { useState, useMemo, useEffect } from "react";
import LoadingSkeleton from "../general/skeleton";
import { Copy } from "lucide-react";
import { copyToClipboard } from "../../utils";
import { matchesAny } from "../hooks/matchesAny";

export const FullViewTableCard = ({
  title,
  columns,
  data = [],
  loading,
  rowsPerPage = 10,
  actions = [],
  textFilterColumns = [], // e.g. ["name", "email", "code"]
  dateFilterColumns = [], // e.g. ["createdAt", "updatedAt"]
  monthFilter = null,
  hidden
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [textFilter, setTextFilter] = useState("");
  const [dateFilters, setDateFilters] = useState({});

  useEffect(() => {
    setCurrentPage(1);
  }, [data]); // { createdAt: "2025-10-10", updatedAt: "2025-10-09" }

  // Pagination handlers
  const nextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  // Format date to "Month Day, Year" and ignore any time component
  const formatDatePretty = (val) => {
    if (!val) return "";
    const date = new Date(val);

    // If parsing failed, try removing any "at" or time-like text first
    if (isNaN(date)) {
      const clean = String(val)
        .replace(/at\s+\d{1,2}:\d{2}(:\d{2})?\s?(AM|PM)?/i, "") // remove "at 3:55:05 PM"
        .replace(/\d{1,2}:\d{2}(:\d{2})?\s?(AM|PM)?/i, "") // remove "15:55:05"
        .trim();
      const retry = new Date(clean);
      if (!isNaN(retry)) {
        return retry.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
      return clean;
    }

    // Normal successful parse
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Render cell content - handle links
  const renderCellContent = (value) => {
    // Check if value is a link object with { text, href, onClick }
    if (value && typeof value === 'object' && (value.href || value.onClick)) {
      return (
        <a
          href={value.href || '#'}
          onClick={(e) => {
            if (value.onClick) {
              e.preventDefault();
              value.onClick();
            }
          }}
          className="font-bold text-primary-title hover:underline cursor-pointer"
        >
          {value.text}
        </a>
      );
    }
    return value;
  };

  const getCellValue = (cell) => {
    if (cell && typeof cell === "object") {
      return cell.raw ?? cell.display ?? cell.text ?? cell;
    }

    return cell;
  };

  // Filtering logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      let matchesText = true;
      let matchesDates = true;

      // Text filter (search across multiple text columns)
      if (textFilter && textFilterColumns.length > 0) {
        matchesText = textFilterColumns.some((col) => {
          const cell = row[col];
          
          return String(getCellValue(cell) ?? "").toLowerCase().includes(textFilter.toLowerCase());
        });
      }

      // Date filters (each column must match its date if set)
      if (dateFilterColumns.length > 0) {
        matchesDates = Object.entries(dateFilters).every(([col, filterDate]) => {
          if (!filterDate) return true;
          const cell = row[col];
          const formattedCell = formatDatePretty(getCellValue(cell));

          const formattedFilter = formatDatePretty(filterDate);
          return formattedCell === formattedFilter;
        });
      }

      return matchesText && matchesDates;
    });
  }, [data, textFilter, dateFilters, textFilterColumns, dateFilterColumns]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  // Dynamic placeholder
  const textPlaceholder =
    textFilterColumns.length > 0
      ? `Search ${textFilterColumns.join(", ")}...`
      : "Search...";

  // Handle date filter changes
  const handleDateChange = (col, value) => {
    setDateFilters((prev) => ({ ...prev, [col]: value }));
    setCurrentPage(1);
  };

  return (
    <LoadingSkeleton loading={loading}>
      <div className="w-full bg-white flex-1 px-3 py-4 rounded-2xl font-nexa flex flex-col overflow-x-auto
       max-w-[380px] sm:max-w-[600px] md:max-w-[800px] lg:max-w-[1200px] xl:max-w-[100%]">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-nexa text-lg text-primary-dark leading-10 m-0">{title}</h2>
          {actions.length > 0 && (
            <div className="flex gap-2">
              {actions.map((action, index) => (
                <div key={index}>{action}</div>
              ))}
            </div>
          )}
        </div>

        <div className={`${hidden && "blur-sm"}`}>
          {/* Filters Section */}
          <div className="flex flex-wrap gap-3 mb-4">
            {/* Global Text Filter */}
            {textFilterColumns.length > 0 && (
              <input
                type="text"
                value={textFilter}
                onChange={(e) => {
                  setTextFilter(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={textPlaceholder}
                style={{
                  width: `min(${Math.max(textPlaceholder.length, 20)}ch, 100%)`,
                }}
                className=" px-3 py-2 border rounded-md text-sm"
              />
            )}

            {/* Date Filters */}
            {dateFilterColumns.map((col) => (
              <div key={col} className="flex items-center gap-2">
                <label className="text-sm text-primary-dark">{col}:</label>
                <input
                  type="date"
                  value={dateFilters[col] || ""}
                  onChange={(e) => handleDateChange(col, e.target.value)}
                  className="px-2 py-2 border rounded-md text-sm"
                  placeholder={col}
                />
              </div>
            ))}

            {/* Month Filter slot */}
            {monthFilter}
          </div>

          {/* Table */}
          <div className="w-full overflow-x-auto">
            <table className="border-collapse table-auto w-full">
              <thead>
                <tr className="text-primary-title text-sm">
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      className="px-4 py-3 text-left uppercase border-b border-subText"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentRows.length > 0 ? (
                  currentRows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`border-b border-subText hover:bg-gray-100 transition ${
                        rowIndex === currentRows.length - 1
                          ? "border-none font-bold text-primary-dark"
                          : ""
                      }`}
                    >
                      {columns.map((col, cellIndex) => {
                        const cellValue = row[col];
                        const isCodeColumn = matchesAny(col, ["Code", "Token", "Reference", "Meter SN", "Purchase ID"]);
                        const displayValue =
                          typeof cellValue === "object" && cellValue?.display !== undefined
                            ? cellValue.display
                            : cellValue;
                        const rawValue = getCellValue(cellValue);

                        return (
                          <td
                            key={cellIndex}
                            className="px-2 py-3 text-sm text-primary-dark break-words whitespace-wrap"
                          >
                            <span className="flex items-center gap-2">
                              {renderCellContent(displayValue)}
                              {isCodeColumn && (
                                <Copy
                                  size={32}
                                  className="inline-block cursor-pointer h-5 w-5 rounded-md hover:bg-primary-gray/20"
                                  onClick={() => copyToClipboard(rawValue)}
                                />
                              )}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="px-4 py-3 text-center text-gray-500"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded-md ${
                  currentPage === 1
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "text-primary-title border border-primary-title hover:bg-primaryDark hover:text-white transition-all"
                }`}
              >
                Previous
              </button>
              <span className="text-sm text-primary-dark">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`px-2 py-1 rounded-md ${
                  currentPage === totalPages
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "text-primary-title border border-primary-title hover:bg-primaryDark hover:text-white transition-all"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>

      </div>
    </LoadingSkeleton>
  );
};

export default FullViewTableCard;
